import type { QueryClient, FetchQueryOptions } from "@tanstack/react-query"

/** Longest a route waits for its data before painting with what it has —
 *  under the router's 1s pending threshold, so its skeleton never flashes. */
const DEFAULT_MAX_WAIT_MS = 900

/**
 * Loads the queries a page shows at first paint, all in parallel, so the
 * page renders WITH its data instead of painting skeletons and filling in
 * card by card a moment later.
 *
 *  · Never throws — a failing request is the page's own error state to show,
 *    not a reason to block or break navigation.
 *  · Waits at most `maxWaitMs`: one slow endpoint can't hold the whole page
 *    hostage. Requests still in flight keep going and land in the cache, and
 *    the components pick them up the moment they arrive.
 *  · Uses `ensureQueryData`, so fresh cached data (a revisit, or a hover
 *    preload) resolves instantly with no request at all.
 *
 * Pass the SAME `queryOptions()` the components use, or the cache misses
 * and the page fetches twice.
 */
export async function warm(
  queryClient: QueryClient,
  // Each entry is any `queryOptions(...)` result; the data type doesn't matter here.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  queries: (FetchQueryOptions<any, any, any, any> | false | null | undefined)[],
  maxWaitMs = DEFAULT_MAX_WAIT_MS
): Promise<void> {
  const loads = queries
    .filter((q): q is FetchQueryOptions => Boolean(q))
    .map((q) => queryClient.ensureQueryData(q).catch(() => undefined))
  if (loads.length === 0) return
  let timer: ReturnType<typeof setTimeout> | undefined
  await Promise.race([
    Promise.all(loads),
    new Promise<void>((resolve) => {
      timer = setTimeout(resolve, maxWaitMs)
    }),
  ])
  clearTimeout(timer)
}
